import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { Icons } from 'utoniq-ui'

import { Gacha } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'

type GachaBoxProps = {
  gacha: Pick<
    Gacha,
    'id' | 'thumbnail' | 'name' | 'formattedEndAt' | 'gpsData' | 'isPremium'
  >
  artistId?: string
  fullWidth?: boolean
}

export const GachaBox = ({ gacha, artistId, fullWidth }: GachaBoxProps) => {
  const { t } = useTranslation('globalHome')
  const { routerPages, generatePath } = useRouter()
  const href = useMemo<string>(() => {
    if (artistId) {
      return generatePath(routerPages.ArtistGachaDetail, {
        gachaId: gacha.id,
        artistId,
      })
    }
    return generatePath(routerPages.ArtistGachaDetail, { gachaId: gacha.id })
  }, [artistId, gacha.id, generatePath, routerPages.ArtistGachaDetail])

  return (
    <Card
      sx={{
        width: fullWidth ? '100%' : 315,
        background: 'none',
        boxShadow: 'none',
        borderRadius: 2,
      }}
    >
      <CardActionArea href={href}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: fullWidth ? '40.6% 1fr' : '136px 1fr',
            gap: 4,
          }}
        >
          <Box
            sx={{
              // HACK: 画像アスペクト比を4:3に固定する
              position: 'relative',
              pt: '75%',
            }}
          >
            <CardMedia
              component="img"
              alt={gacha.name}
              image={gacha.thumbnail.smallUri || ''}
              sx={{
                borderRadius: 2,
                aspectRatio: '4 / 3',
                position: 'absolute',
                top: 0,
                left: 0,
                width: 1,
                height: 1,
              }}
            />
          </Box>
          <Stack spacing={2} justifyContent="center">
            {!!(gacha.gpsData || gacha.isPremium) && (
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                {gacha.gpsData ? <CheckInChip /> : <Box />}
                {gacha.isPremium && (
                  <Icons.MembersOnly
                    fontSize="medium"
                    sx={{ color: 'info.main' }}
                  />
                )}
              </Stack>
            )}
            <Stack>
              <Typography variant="h5" sx={styleSets.lineCamp(2)}>
                {gacha.name}
              </Typography>
              {gacha.formattedEndAt && (
                <Typography variant="body2" color="text.hint">
                  {t('until', {
                    string: gacha.formattedEndAt,
                    interpolation: { escapeValue: false },
                  })}
                </Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  )
}

const CheckInChip: Cell = () => {
  return (
    <CellFrame>
      <Chip
        label={
          <Typography variant="subtitle2" color="white" display="block">
            CHECK-IN
          </Typography>
        }
        sx={{
          borderRadius: '2px',
          height: 20,
          bgcolor: 'secondary.dark',
          '.MuiChip-label': {
            px: 1,
          },
        }}
      />
    </CellFrame>
  )
}
